package com.snakesworld.game.engine

import com.snakesworld.game.domain.CollisionDetection
import com.snakesworld.game.domain.DeathDropCalculator
import com.snakesworld.game.domain.HeadToHeadResult
import com.snakesworld.game.domain.PelletSpawnCalculator
import com.snakesworld.game.domain.SnakeAiController
import com.snakesworld.game.domain.SnakeKinematics
import com.snakesworld.game.network.DeathCause
import com.snakesworld.game.network.NicknameValidator
import com.snakesworld.game.network.PelletNetworkState
import com.snakesworld.game.network.SessionResult
import com.snakesworld.game.network.SnakeNetworkState
import kotlin.random.Random

class GameEngine(
    val localPlayerId: String,
    val localPlayerName: String,
    val skinId: String = "neon_viper",
) {
    var localSnake: SnakePlayer = run {
        val startPos = generateRandomSpawnPosition()
        val initialCount = GameConstants.INITIAL_SNAKE_BODY_SEGMENTS
        SnakePlayer(
            id = localPlayerId,
            name = localPlayerName,
            skinId = skinId,
            headPosition = startPos,
            direction = Direction.RIGHT,
            segments = SnakeKinematics.createInitialSegments(startPos, Direction.RIGHT, initialCount),
            mass = GameConstants.INITIAL_SNAKE_MASS,
            speed = GameConstants.BASE_SNAKE_SPEED,
            joinedAt = 0L,
        )
    }
        private set

    private var moveAccumulator: Float = 0f
    private var lastSteppedDirection: Direction = Direction.RIGHT
    private var queuedDirection: Direction? = null
    private var nextQueuedDirection: Direction? = null
    private var isBoostRequested: Boolean = false

    private val remoteSnakes = mutableMapOf<String, SnakePlayer>()
    private val remoteMoveAccumulators = mutableMapOf<String, Float>()
    private val remoteTargetStates = mutableMapOf<String, SnakeNetworkState>()
    private val aiSpecs = mutableListOf<AiBotSpec>()
    private val aiLastStepped = mutableMapOf<String, Direction>()
    private val aiRespawnAtMs = mutableMapOf<String, Long>()

    private val pellets = mutableMapOf<String, FoodPellet>()
    private val activeKillEvents = mutableListOf<KillEvent>()
    /** Victim ids already credited from death-orb `kb`, so one corpse cannot score twice. */
    private val creditedKillVictims = mutableSetOf<String>()

    var isGameOver: Boolean = false
        private set
    var gameOverResult: SessionResult? = null
        private set
    var isSinglePlayer: Boolean = false
        private set
    var isPaused: Boolean = false
        private set
    private var pauseStartTimeMs: Long = 0L

    private var matchStartTimeMs: Long = 0L

    fun generateRandomSpawnPosition(range: Float = GameConstants.ARENA_HALF_SIZE * 0.6f): Vector2D =
        Companion.generateRandomSpawnPosition(range)

    fun initMatch(startPosition: Vector2D? = null, timestampMs: Long = 0L) {
        val spawn = CollisionDetection.snapToGrid(startPosition ?: generateRandomSpawnPosition())
        matchStartTimeMs = timestampMs
        val targetCount = GameConstants.INITIAL_SNAKE_BODY_SEGMENTS
        val initialSegments = SnakeKinematics.createInitialSegments(
            headPosition = spawn,
            direction = Direction.RIGHT,
            count = targetCount,
            spacing = GameConstants.SEGMENT_SPACING,
        )
        localSnake = localSnake.copy(
            headPosition = spawn,
            direction = Direction.RIGHT,
            segments = initialSegments,
            mass = GameConstants.INITIAL_SNAKE_MASS,
            speed = GameConstants.BASE_SNAKE_SPEED,
            isBoosting = false,
            isAlive = true,
            score = 0,
            kills = 0,
            joinedAt = timestampMs,
            lastHeartbeat = timestampMs,
        )
        lastSteppedDirection = Direction.RIGHT
        queuedDirection = null
        nextQueuedDirection = null
        moveAccumulator = 0f
        isBoostRequested = false
        isGameOver = false
        gameOverResult = null
        isPaused = false
        pauseStartTimeMs = 0L
        if (isSinglePlayer) {
            setupSinglePlayerMatch()
        }
    }

    fun pause(timestampMs: Long = 0L) {
        if (!isSinglePlayer || isPaused) return
        isPaused = true
        isBoostRequested = false
        localSnake = localSnake.copy(isBoosting = false)
        pauseStartTimeMs = timestampMs
    }

    fun resume(timestampMs: Long = 0L) {
        if (!isPaused) return
        isPaused = false
        if (pauseStartTimeMs > 0L && timestampMs > pauseStartTimeMs) {
            val pausedDuration = timestampMs - pauseStartTimeMs
            if (matchStartTimeMs > 0L) {
                matchStartTimeMs += pausedDuration
            }
            for ((botId, respawnTime) in aiRespawnAtMs.toList()) {
                aiRespawnAtMs[botId] = respawnTime + pausedDuration
            }
        }
        pauseStartTimeMs = 0L
    }

    fun setSinglePlayerMode(enabled: Boolean) {
        isSinglePlayer = enabled
    }

    companion object {
        fun generateRandomSpawnPosition(range: Float = GameConstants.ARENA_HALF_SIZE * 0.6f): Vector2D {
            val spawnX = (Random.nextFloat() * 2f - 1f) * range
            val spawnY = (Random.nextFloat() * 2f - 1f) * range
            return CollisionDetection.snapToGrid(Vector2D(spawnX, spawnY))
        }
    }

    fun setQueuedDirection(desired: Direction) {
        if (isPaused) return
        val currentHeadDir = localSnake.direction
        if (desired == currentHeadDir) return

        if (SnakeKinematics.isValidTurn(lastSteppedDirection, desired) &&
            SnakeKinematics.isValidTurn(currentHeadDir, desired)
        ) {
            queuedDirection = desired
            nextQueuedDirection = null
            // Visually orient the snake head toward the queued direction immediately
            localSnake = localSnake.copy(direction = desired)
        } else if (queuedDirection != null && SnakeKinematics.isValidTurn(queuedDirection!!, desired)) {
            // Buffer the secondary turn for the next tick
            nextQueuedDirection = desired
        }
    }

    fun setBoosting(boosting: Boolean) {
        if (isPaused) return
        isBoostRequested = boosting
    }

    fun updatePelletsFromNetwork(networkPellets: Map<String, PelletNetworkState>) {
        // Pellets are NEVER cleared or truncated. Every pellet in the arena is preserved until eaten.
        for ((id, p) in networkPellets) {
            if (id !in pellets) {
                creditKillFromDeathOrb(id, p)
                pellets[id] = FoodPellet(
                    id = p.id,
                    position = CollisionDetection.clampPelletInsideArena(Vector2D(p.x, p.y)),
                    mass = p.m,
                    color = p.c,
                    isCorpse = p.cp,
                    isBoostTrail = p.bt,
                )
            }
        }

        // A pellet is ONLY removed when it is no longer in networkPellets (meaning a snake ate it)
        // or eaten locally, preserving unsynced local boost trails.
        val toRemove = pellets.keys.filter { key ->
            key !in networkPellets.keys && !key.startsWith("trail_${localPlayerId}_")
        }
        for (key in toRemove) {
            pellets.remove(key)
        }
    }

    fun updateRemoteSnakesFromNetwork(networkSnakes: Map<String, SnakeNetworkState>) {
        // Remove departed snakes
        val departed = (remoteSnakes.keys - networkSnakes.keys).toList()
        for (id in departed) {
            remoteSnakes.remove(id)
            remoteMoveAccumulators.remove(id)
            remoteTargetStates.remove(id)
        }

        // Add or update existing
        for ((id, netState) in networkSnakes) {
            if (id == localPlayerId) continue
            remoteTargetStates[id] = netState

            val existing = remoteSnakes[id]
            if (existing == null) {
                val newPos = CollisionDetection.snapToGrid(Vector2D(netState.x, netState.y))
                val newDir = Direction.fromAngle(netState.a)
                val targetCount = (netState.m - 1f).toInt().coerceAtLeast(GameConstants.MIN_SNAKE_BODY_SEGMENTS)
                val segments = SnakeKinematics.createInitialSegments(
                    headPosition = newPos,
                    direction = newDir,
                    count = targetCount,
                    spacing = GameConstants.SEGMENT_SPACING,
                )
                    remoteSnakes[id] = SnakePlayer(
                    id = id,
                    name = NicknameValidator.forDisplay(netState.n),
                    skinId = netState.sk,
                    headPosition = newPos,
                    direction = newDir,
                    segments = segments,
                    mass = netState.m,
                    speed = netState.s,
                    isBoosting = netState.b,
                    isAlive = true,
                    joinedAt = netState.ts,
                    lastHeartbeat = netState.ts,
                )
            } else {
                val shown = NicknameValidator.forDisplay(netState.n)
                if (existing.name != shown) {
                    remoteSnakes[id] = existing.copy(name = shown)
                }
            }
        }
    }

    data class UpdateResult(
        val consumedPelletIds: List<String>,
        val spawnedTrailPellets: List<PelletNetworkState>,
        val deathDrops: List<PelletNetworkState>,
        val killerId: String?,
        val justDied: Boolean,
        val atePellet: Boolean,
        /** Uid that should receive a kill. Null for walls, self-hits, and head-to-head. */
        val killerPlayerId: String? = null,
    )

    /** Body cells of a remote snake, for the elder's disconnect corpse drop. */
    fun corpseOrbsFor(playerId: String): List<PelletNetworkState> {
        val snake = remoteSnakes[playerId] ?: return emptyList()
        return DeathDropCalculator.generateDeathOrbs(
            victimId = playerId,
            headPosition = snake.headPosition,
            segments = snake.segments,
        )
    }

    fun tick(deltaTime: Float, currentTimestampMs: Long): UpdateResult {
        if (isPaused) {
            return UpdateResult(emptyList(), emptyList(), emptyList(), null, justDied = false, atePellet = false)
        }
        if (matchStartTimeMs == 0L && currentTimestampMs > 0L) {
            matchStartTimeMs = currentTimestampMs
        }
        if (isGameOver || !localSnake.isAlive) {
            return UpdateResult(emptyList(), emptyList(), emptyList(), null, justDied = false, atePellet = false)
        }

        // 1. Process Boost (Sprint speed without draining mass or shortening length)
        val canBoost = isBoostRequested
        val currentSpeed = if (canBoost) {
            GameConstants.BASE_SNAKE_SPEED * GameConstants.BOOST_SPEED_MULTIPLIER
        } else {
            GameConstants.BASE_SNAKE_SPEED
        }

        val newMass = localSnake.mass
        val spawnedTrailPellets = emptyList<PelletNetworkState>()

        // 2. Move Local Snake (Discrete steps: moves every 0.25s, or 0.125s when sprint boosting)
        val stepInterval = if (canBoost) {
            GameConstants.BASE_MOVE_INTERVAL_SECONDS / GameConstants.BOOST_SPEED_MULTIPLIER
        } else {
            GameConstants.BASE_MOVE_INTERVAL_SECONDS
        }
        moveAccumulator += deltaTime
        val stepsToTake = (moveAccumulator / stepInterval).toInt().coerceAtMost(5)
        if (stepsToTake > 0) {
            moveAccumulator -= stepsToTake * stepInterval
        }

        for (step in 0 until stepsToTake) {
            val stepDir = queuedDirection ?: lastSteppedDirection
            lastSteppedDirection = stepDir
            queuedDirection = nextQueuedDirection
            nextQueuedDirection = null

            // Boosting never shortens the snake's length or drains mass
            val targetCount = maxOf(localSnake.segments.size, (newMass - 1f).toInt().coerceAtLeast(GameConstants.MIN_SNAKE_BODY_SEGMENTS))
            val (newHead, updatedSegments) = SnakeKinematics.advanceSnake(
                currentHead = localSnake.headPosition,
                direction = stepDir,
                segments = localSnake.segments,
                targetSegmentCount = targetCount,
                stepSize = GameConstants.SEGMENT_SPACING,
            )

            localSnake = localSnake.copy(
                headPosition = newHead,
                direction = queuedDirection ?: stepDir,
                segments = updatedSegments,
                mass = newMass,
                speed = currentSpeed,
                isBoosting = canBoost,
                lastHeartbeat = currentTimestampMs,
            )
        }

        if (stepsToTake == 0) {
            localSnake = localSnake.copy(
                mass = newMass,
                speed = currentSpeed,
                isBoosting = canBoost,
                lastHeartbeat = currentTimestampMs,
            )
        }

        // 3. Update Remote Snakes (network interpolation) or local AI bots
        if (isSinglePlayer) {
            tickLocalAiSnakes(deltaTime, currentTimestampMs)
        } else {
            for (id in remoteSnakes.keys.toList()) {
                val remote = remoteSnakes[id] ?: continue
                val targetState = remoteTargetStates[id]
                if (targetState != null) {
                    val targetPos = CollisionDetection.snapToGrid(Vector2D(targetState.x, targetState.y))
                    val targetDir = Direction.fromAngle(targetState.a)
                    val remoteStepInterval = if (targetState.b) {
                        GameConstants.BASE_MOVE_INTERVAL_SECONDS / GameConstants.BOOST_SPEED_MULTIPLIER
                    } else {
                        GameConstants.BASE_MOVE_INTERVAL_SECONDS
                    }
                    val acc = (remoteMoveAccumulators[id] ?: 0f) + deltaTime
                    val remoteSteps = (acc / remoteStepInterval).toInt().coerceAtMost(5)
                    remoteMoveAccumulators[id] = if (remoteSteps > 0) acc - remoteSteps * remoteStepInterval else acc

                    var currentRemote = remote
                    for (s in 0 until remoteSteps) {
                        val (newHead, newSegs) = SnakeKinematics.advanceSnake(
                            currentHead = currentRemote.headPosition,
                            direction = targetDir,
                            segments = currentRemote.segments,
                            targetSegmentCount = (targetState.m - 1f).toInt().coerceAtLeast(GameConstants.MIN_SNAKE_BODY_SEGMENTS),
                            stepSize = GameConstants.SEGMENT_SPACING,
                        )
                        currentRemote = currentRemote.copy(
                            headPosition = newHead,
                            direction = targetDir,
                            segments = newSegs,
                            mass = targetState.m,
                            speed = if (targetState.b) GameConstants.BASE_SNAKE_SPEED * GameConstants.BOOST_SPEED_MULTIPLIER else GameConstants.BASE_SNAKE_SPEED,
                            isBoosting = targetState.b,
                            lastHeartbeat = targetState.ts,
                        )
                    }

                    if (currentRemote.headPosition.distanceTo(targetPos) > GameConstants.SEGMENT_SPACING * 3f) {
                        currentRemote = currentRemote.copy(headPosition = targetPos)
                    }

                    remoteSnakes[id] = currentRemote
                }
            }
        }

        // 5. Pellet Consumption
        val consumedPelletIds = mutableListOf<String>()
        var gainedMass = 0f
        var ateAny = false

        for (pellet in pellets.values.toList()) {
            if (CollisionDetection.checkPelletPickup(localSnake.headPosition, pellet.position)) {
                consumedPelletIds.add(pellet.id)
                gainedMass += 1f
                ateAny = true
            }
        }

        for (id in consumedPelletIds) {
            pellets.remove(id)
        }

        if (gainedMass > 0f) {
            val updatedMass = localSnake.mass + gainedMass
            val updatedScore = localSnake.score + consumedPelletIds.size
            localSnake = localSnake.copy(
                mass = updatedMass,
                score = updatedScore,
            )
        }

        if (isSinglePlayer) {
            consumePelletsForAiSnakes()
            replenishAmbientPelletsIfNeeded()
        }

        // 6. Collision Resolution
        var deathReasonKiller: String? = null
        var killerPlayerId: String? = null
        var killerDisplayId: String? = null
        var deathCause = DeathCause.ArenaBoundary
        var perished = false

        // A) Boundary Collision
        if (CollisionDetection.isOutOfBounds(localSnake.headPosition)) {
            perished = true
            deathCause = DeathCause.ArenaBoundary
            deathReasonKiller = null
        }

        // B) Self-Collision (skip neck; same rectangular grid cell only)
        if (!perished && CollisionDetection.checkSelfCollision(localSnake.headPosition, localSnake.segments)) {
            perished = true
            deathCause = DeathCause.SelfCollision
            deathReasonKiller = null
        }

        // C) Opponent Body Predation
        if (!perished) {
            for ((oppId, opponent) in remoteSnakes.toMap()) {
                if (CollisionDetection.checkOpponentBodyCollision(localSnake.headPosition, opponent.segments)) {
                    perished = true
                    deathCause = DeathCause.Opponent
                    deathReasonKiller = opponent.name
                    killerPlayerId = oppId
                    killerDisplayId = oppId
                    break
                }
            }
        }

        // D) Head-to-head: same cell is always mutual demise (mass does not decide).
        // killerPlayerId stays null so no kill is credited. killerDisplayId only
        // hides that nickname on the game-over line when the player is blocked.
        if (!perished) {
            for ((oppId, opponent) in remoteSnakes.toMap()) {
                if (CollisionDetection.checkHeadToHeadCollision(
                        localSnake.headPosition,
                        opponent.headPosition,
                    ) == HeadToHeadResult.MutualDemise
                ) {
                    perished = true
                    deathCause = DeathCause.Opponent
                    deathReasonKiller = opponent.name
                    killerDisplayId = oppId
                    break
                }
            }
        }

        val deathDrops = if (perished) {
            isGameOver = true
            localSnake = localSnake.copy(isAlive = false)
            val survivedSec = if (matchStartTimeMs > 0L) (currentTimestampMs - matchStartTimeMs) / 1000L else 0L
            gameOverResult = SessionResult(
                score = localSnake.score,
                kills = localSnake.kills,
                length = localSnake.segments.size + 1,
                survivedSeconds = survivedSec,
                killerName = deathReasonKiller,
                killerId = killerDisplayId,
                deathCause = deathCause,
            )
            DeathDropCalculator.generateDeathOrbs(
                victimId = localPlayerId,
                headPosition = localSnake.headPosition,
                segments = localSnake.segments,
            )
        } else {
            emptyList()
        }

        if (isSinglePlayer) {
            resolveAiCollisions(currentTimestampMs)
        }

        return UpdateResult(
            consumedPelletIds = consumedPelletIds,
            spawnedTrailPellets = spawnedTrailPellets,
            deathDrops = deathDrops,
            killerId = deathReasonKiller,
            justDied = perished,
            atePellet = ateAny,
            killerPlayerId = killerPlayerId,
        )
    }

    fun getSnapshot(): GameState {
        val allSnakes = mutableListOf<LeaderboardEntry>()
        allSnakes.add(
            LeaderboardEntry(
                id = localPlayerId,
                name = NicknameValidator.forDisplay(localPlayerName),
                score = localSnake.score,
                kills = localSnake.kills,
                skinId = localSnake.skinId,
            )
        )
        for (r in remoteSnakes.values.toList()) {
            allSnakes.add(
                LeaderboardEntry(
                    id = r.id,
                    name = NicknameValidator.forDisplay(r.name),
                    score = if (isSinglePlayer) {
                        r.score
                    } else {
                        (r.mass - GameConstants.INITIAL_SNAKE_MASS).toInt().coerceAtLeast(0)
                    },
                    kills = r.kills,
                    skinId = r.skinId,
                )
            )
        }
        allSnakes.sortByDescending { it.score }

        return GameState(
            localPlayer = localSnake,
            remoteSnakes = remoteSnakes.toMap(),
            pellets = pellets.toMap(),
            leaderboard = allSnakes.take(10),
            killEvents = activeKillEvents.takeLast(5),
            isGameOver = isGameOver,
            gameOverStats = gameOverResult,
            isPaused = isPaused,
        )
    }

    private data class AiBotSpec(
        val id: String,
        val name: String,
        val skinId: String,
    )

    private fun setupSinglePlayerMatch() {
        remoteSnakes.clear()
        remoteMoveAccumulators.clear()
        remoteTargetStates.clear()
        aiLastStepped.clear()
        aiRespawnAtMs.clear()
        aiSpecs.clear()
        pellets.clear()
        activeKillEvents.clear()

        val skins = SNAKE_SKIN_OPTIONS.filter { it.id != skinId }.ifEmpty { SNAKE_SKIN_OPTIONS }
        val avoid = mutableListOf(localSnake.headPosition)
        for (i in 0 until SnakeAiController.BOT_COUNT) {
            val spec = AiBotSpec(
                id = "ai_bot_${i + 1}",
                name = SnakeAiController.BOT_NAMES[i % SnakeAiController.BOT_NAMES.size],
                skinId = skins[i % skins.size].id,
            )
            aiSpecs.add(spec)
            val spawned = spawnAiBot(spec, avoid)
            avoid.add(spawned.headPosition)
        }
        addPelletsFromNetwork(
            PelletSpawnCalculator.generateAmbientPellets(
                count = GameConstants.BASE_PELLET_COUNT,
                idPrefix = "sp",
            )
        )
    }

    private fun spawnAiBot(spec: AiBotSpec, avoid: List<Vector2D>): SnakePlayer {
        val spawn = spawnAwayFrom(avoid)
        val dir = Direction.entries[Random.nextInt(Direction.entries.size)]
        val segments = SnakeKinematics.createInitialSegments(
            headPosition = spawn,
            direction = dir,
            count = GameConstants.INITIAL_SNAKE_BODY_SEGMENTS,
            spacing = GameConstants.SEGMENT_SPACING,
        )
        val bot = SnakePlayer(
            id = spec.id,
            name = spec.name,
            skinId = spec.skinId,
            headPosition = spawn,
            direction = dir,
            segments = segments,
            mass = GameConstants.INITIAL_SNAKE_MASS,
            speed = GameConstants.BASE_SNAKE_SPEED,
            isAlive = true,
            score = 0,
            kills = 0,
        )
        remoteSnakes[spec.id] = bot
        aiLastStepped[spec.id] = dir
        remoteMoveAccumulators[spec.id] = 0f
        aiRespawnAtMs.remove(spec.id)
        return bot
    }

    private fun spawnAwayFrom(avoid: List<Vector2D>): Vector2D {
        var best = generateRandomSpawnPosition()
        var bestMin = 0f
        repeat(16) {
            val candidate = generateRandomSpawnPosition()
            val minDist = avoid.minOfOrNull { it.distanceTo(candidate) } ?: Float.MAX_VALUE
            if (minDist >= SnakeAiController.SPAWN_MIN_SEPARATION) return candidate
            if (minDist > bestMin) {
                bestMin = minDist
                best = candidate
            }
        }
        return best
    }

    private fun tickLocalAiSnakes(deltaTime: Float, currentTimestampMs: Long) {
        for (spec in aiSpecs) {
            val respawnAt = aiRespawnAtMs[spec.id]
            if (respawnAt != null && currentTimestampMs >= respawnAt) {
                val avoid = mutableListOf(localSnake.headPosition)
                avoid.addAll(remoteSnakes.values.map { it.headPosition })
                spawnAiBot(spec, avoid)
            }
        }

        for (id in remoteSnakes.keys.toList()) {
            val bot = remoteSnakes[id] ?: continue
            if (!bot.isAlive) continue

            val acc = (remoteMoveAccumulators[id] ?: 0f) + deltaTime
            val steps = (acc / GameConstants.BASE_MOVE_INTERVAL_SECONDS).toInt().coerceAtMost(5)
            remoteMoveAccumulators[id] =
                if (steps > 0) acc - steps * GameConstants.BASE_MOVE_INTERVAL_SECONDS else acc

            var current = bot
            var lastStepped = aiLastStepped[id] ?: bot.direction
            for (s in 0 until steps) {
                val others = buildList {
                    add(localSnake)
                    remoteSnakes.values.filter { it.id != id && it.isAlive }.forEach { add(it) }
                }
                val chosen = SnakeAiController.chooseDirection(
                    lastStepped = lastStepped,
                    head = current.headPosition,
                    segments = current.segments,
                    pellets = pellets.values,
                    otherSnakes = others,
                )
                val targetCount = maxOf(
                    current.segments.size,
                    (current.mass - 1f).toInt().coerceAtLeast(GameConstants.MIN_SNAKE_BODY_SEGMENTS),
                )
                val (newHead, newSegs) = SnakeKinematics.advanceSnake(
                    currentHead = current.headPosition,
                    direction = chosen,
                    segments = current.segments,
                    targetSegmentCount = targetCount,
                    stepSize = GameConstants.SEGMENT_SPACING,
                )
                lastStepped = chosen
                current = current.copy(
                    headPosition = newHead,
                    direction = chosen,
                    segments = newSegs,
                    speed = GameConstants.BASE_SNAKE_SPEED,
                    lastHeartbeat = currentTimestampMs,
                )
            }
            if (steps == 0) {
                current = current.copy(lastHeartbeat = currentTimestampMs)
            }
            aiLastStepped[id] = lastStepped
            remoteSnakes[id] = current
        }
    }

    private fun consumePelletsForAiSnakes() {
        for (id in remoteSnakes.keys.toList()) {
            val bot = remoteSnakes[id] ?: continue
            if (!bot.isAlive) continue
            var gained = 0
            val eaten = mutableListOf<String>()
            for (pellet in pellets.values.toList()) {
                if (CollisionDetection.checkPelletPickup(bot.headPosition, pellet.position)) {
                    eaten.add(pellet.id)
                    gained += 1
                }
            }
            for (pelletId in eaten) {
                pellets.remove(pelletId)
            }
            if (gained > 0) {
                remoteSnakes[id] = bot.copy(
                    mass = bot.mass + gained.toFloat(),
                    score = bot.score + gained,
                )
            }
        }
    }

    private fun resolveAiCollisions(currentTimestampMs: Long) {
        for (id in remoteSnakes.keys.toList()) {
            val bot = remoteSnakes[id] ?: continue
            if (!bot.isAlive) continue

            var killerName: String? = null
            var killerId: String? = null
            var perished = false

            if (CollisionDetection.isOutOfBounds(bot.headPosition) ||
                CollisionDetection.checkSelfCollision(bot.headPosition, bot.segments)
            ) {
                perished = true
            }

            if (!perished && CollisionDetection.checkOpponentBodyCollision(bot.headPosition, localSnake.segments)) {
                perished = true
                killerId = localPlayerId
                killerName = localPlayerName
            }

            if (!perished) {
                for ((oppId, opponent) in remoteSnakes.toMap()) {
                    if (oppId == id || !opponent.isAlive) continue
                    if (CollisionDetection.checkOpponentBodyCollision(bot.headPosition, opponent.segments)) {
                        perished = true
                        killerId = oppId
                        killerName = opponent.name
                        break
                    }
                }
            }

            if (!perished) {
                if (CollisionDetection.checkHeadToHeadCollision(
                        bot.headPosition,
                        localSnake.headPosition,
                    ) == HeadToHeadResult.MutualDemise
                ) {
                    perished = true
                    // Mutual crash — mass does not award a kill
                    killerId = null
                    killerName = null
                }
            }

            if (!perished) {
                for ((oppId, opponent) in remoteSnakes.toMap()) {
                    if (oppId == id || !opponent.isAlive) continue
                    if (CollisionDetection.checkHeadToHeadCollision(
                            bot.headPosition,
                            opponent.headPosition,
                        ) == HeadToHeadResult.MutualDemise
                    ) {
                        perished = true
                        killerId = null
                        killerName = null
                        break
                    }
                }
            }

            if (perished) {
                killAiBot(bot, currentTimestampMs, killerId, killerName)
            }
        }
    }

    private fun killAiBot(
        bot: SnakePlayer,
        currentTimestampMs: Long,
        killerId: String?,
        killerName: String?,
    ) {
        removeAiBotAndDropOrbs(bot, currentTimestampMs)

        if (killerId == localPlayerId) {
            localSnake = localSnake.copy(
                kills = localSnake.kills + 1,
                score = localSnake.score + 500,
            )
        } else if (killerId != null) {
            remoteSnakes[killerId]?.let { killer ->
                remoteSnakes[killerId] = killer.copy(
                    kills = killer.kills + 1,
                    score = killer.score + 500,
                )
            }
        }
        activeKillEvents.add(
            KillEvent(
                killerId = killerId ?: "",
                killerName = killerName ?: "",
                victimId = bot.id,
                victimName = bot.name,
                timestamp = currentTimestampMs,
            )
        )
    }

    private fun removeAiBotAndDropOrbs(bot: SnakePlayer, currentTimestampMs: Long) {
        val orbs = DeathDropCalculator.generateDeathOrbs(
            victimId = bot.id,
            headPosition = bot.headPosition,
            segments = bot.segments,
        )
        addPelletsFromNetwork(orbs)
        remoteSnakes.remove(bot.id)
        remoteMoveAccumulators.remove(bot.id)
        aiLastStepped.remove(bot.id)
        aiRespawnAtMs[bot.id] = currentTimestampMs + SnakeAiController.RESPAWN_DELAY_MS
    }

    private fun replenishAmbientPelletsIfNeeded() {
        val ambient = pellets.values.count { !it.isCorpse && !it.isBoostTrail }
        val playerCount = 1 + remoteSnakes.size
        val target = PelletSpawnCalculator.calculateTargetPelletCount(playerCount)
        val missing = target - ambient
        if (missing >= 8) {
            addPelletsFromNetwork(
                PelletSpawnCalculator.generateAmbientPellets(
                    count = minOf(missing, 15),
                    idPrefix = "sp",
                )
            )
        }
    }

    private fun addPelletsFromNetwork(states: List<PelletNetworkState>) {
        for (p in states) {
            if (p.id !in pellets) {
                pellets[p.id] = FoodPellet(
                    id = p.id,
                    position = CollisionDetection.clampPelletInsideArena(Vector2D(p.x, p.y)),
                    mass = p.m,
                    color = p.c,
                    isCorpse = p.cp,
                    isBoostTrail = p.bt,
                )
            }
        }
    }

    /**
     * A death orb written by the victim carries [PelletNetworkState.kb] set to the killer uid.
     * The killer already listens to pellets, so the credit costs no extra read or write.
     * Head-to-head orbs leave `kb` empty and award nothing.
     */
    private fun creditKillFromDeathOrb(pelletId: String, pellet: PelletNetworkState) {
        if (!pellet.cp || pellet.kb.isBlank() || pellet.kb != localPlayerId) return
        val victimId = deathOrbVictimId(pelletId) ?: return
        if (!creditedKillVictims.add(victimId)) return
        localSnake = localSnake.copy(kills = localSnake.kills + 1)
    }

    private fun deathOrbVictimId(pelletId: String): String? {
        if (!pelletId.startsWith("death_")) return null
        val body = pelletId.removePrefix("death_")
        val split = body.lastIndexOf('_')
        if (split <= 0) return null
        val index = body.substring(split + 1)
        if (index.isEmpty() || index.any { !it.isDigit() }) return null
        return body.substring(0, split)
    }
}
